// Confirmation popover for queueing a manual sample when the user clicks
// anywhere inside a heatmap panel. Renders against a virtual anchor element
// so the caller can position it at the click location.

import { Button, Flex, Popover, Text } from '@radix-ui/themes';
import type { ReactElement, ReactNode, RefObject } from 'react';

import type { AxisSpec } from '../types';

export type ManualSamplePopoverProps = {
  /** The inputs at which to queue the sample. */
  inputs: Record<string, number>;
  /** Full axis specs so we can format units and labels properly. */
  axes: readonly AxisSpec[];
  /** Open flag, controlled by the parent. */
  open: boolean;
  /** Called when the user clicks Confirm. */
  onConfirm: () => void;
  /** Called when the user clicks Cancel or dismisses. */
  onCancel: () => void;
  /**
   * Optional DOM node to anchor against. When provided the popover positions
   * itself near that element (typically a zero-size <span> placed at the
   * click location by the caller).
   */
  anchor?: RefObject<HTMLElement | null>;
  /** Optional trigger element, only used when `anchor` is not supplied. */
  children?: ReactNode;
};

function formatInput(axis: AxisSpec | undefined, value: number): string {
  const precision = Math.abs(value) >= 100 ? 0 : Math.abs(value) >= 10 ? 1 : 2;
  const formatted = value.toFixed(precision);
  const units = axis?.units && axis.units !== 'dimensionless' ? ` ${axis.units}` : '';
  return `${formatted}${units}`;
}

export function ManualSamplePopover(props: ManualSamplePopoverProps): ReactElement {
  const { inputs, axes, open, onConfirm, onCancel, anchor, children } = props;
  const axisByName = new Map(axes.map((a) => [a.name, a]));

  const pairs = Object.entries(inputs).map(([name, value]) => ({
    name,
    value,
    formatted: formatInput(axisByName.get(name), value),
  }));

  const summary = pairs.map((p) => `${p.name} = ${p.formatted}`).join(', ');

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next: boolean) => {
        if (!next) onCancel();
      }}
    >
      {anchor ? (
        <Popover.Anchor virtualRef={anchor as RefObject<HTMLElement>} />
      ) : (
        <Popover.Trigger>{children ?? <span />}</Popover.Trigger>
      )}
      <Popover.Content size="1" style={{ maxWidth: 320 }} data-testid="manual-sample-popover">
        <Flex direction="column" gap="2">
          <Text as="div" size="2" weight="bold">
            Queue manual sample
          </Text>
          <Text as="div" size="2">
            {`Queue manual sample at (${summary})?`}
          </Text>
          <Flex gap="2" justify="end">
            <Button variant="soft" color="gray" size="1" onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="solid" size="1" onClick={onConfirm}>
              Confirm
            </Button>
          </Flex>
        </Flex>
      </Popover.Content>
    </Popover.Root>
  );
}
